"use client"
import { Card, CardContent } from "@/components/ui/card"
import { Loader2, FileImage, CheckCircle } from "lucide-react"
import type { ConversionProgress } from "@/app/utils/image-converter"

interface ImageConversionProgressProps {
  progress: ConversionProgress
  fileName?: string
}

export function ImageConversionProgress({ progress, fileName }: ImageConversionProgressProps) {
  const getStageIcon = () => {
    switch (progress.stage) {
      case "detecting":
        return <FileImage className="h-6 w-6 text-blue-500" />
      case "converting":
        return <Loader2 className="h-6 w-6 text-blue-500 animate-spin" />
      case "validating":
        return <Loader2 className="h-6 w-6 text-green-500 animate-spin" />
      case "complete":
        return <CheckCircle className="h-6 w-6 text-green-500" />
      default:
        return <Loader2 className="h-6 w-6 text-gray-500 animate-spin" />
    }
  }

  const getStageTitle = () => {
    switch (progress.stage) {
      case "detecting":
        return "Analyzing Image"
      case "converting":
        return "Converting HEIC to PNG"
      case "validating":
        return "Validating Result"
      case "complete":
        return "Conversion Complete"
      default:
        return "Processing..."
    }
  }

  return (
    <Card className="w-full max-w-md mx-auto">
      <CardContent className="p-6">
        <div className="flex items-center space-x-4">
          <div className="flex-shrink-0">{getStageIcon()}</div>
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-medium text-gray-900">{getStageTitle()}</h3>
            <p className="text-sm text-gray-600 mt-1">{progress.message}</p>
            {fileName && <p className="text-xs text-gray-500 mt-1 truncate">{fileName}</p>}

            {/* Progress bar */}
            <div className="mt-3">
              <div className="bg-gray-200 rounded-full h-2">
                <div
                  className="bg-blue-500 h-2 rounded-full transition-all duration-300 ease-out"
                  style={{ width: `${progress.progress}%` }}
                />
              </div>
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>{progress.progress}%</span>
                <span>{progress.stage === "complete" ? "Done" : "Processing..."}</span>
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
